<template>
  <div class="paste-tool">
    <!-- Editor card (mock F4): title row → controls row → gutter+code → footer -->
    <template v-if="!pasteResult">
      <div class="editor-card">
        <!-- title -->
        <div class="ec-title-row">
          <span class="ec-label mono">Title</span>
          <input
            v-model="titleInput"
            class="ec-title-input"
            maxlength="255"
            placeholder="Untitled paste"
          />
        </div>

        <!-- controls -->
        <div class="ec-controls">
          <label class="ec-chip">
            <span class="ec-chip-k">Syntax:</span>
            <select v-model="languageInput" class="ec-chip-select">
              <option v-for="l in languages" :key="l.key" :value="l.key">{{ l.label }}</option>
            </select>
            <span class="ec-caret">▾</span>
          </label>
          <label class="ec-chip">
            <span class="ec-chip-k">Expires:</span>
            <select v-model="expiryPreset" class="ec-chip-select">
              <option v-for="p in expiryPresets" :key="p.key" :value="p.key">{{ p.label }}</option>
            </select>
            <span class="ec-caret">▾</span>
          </label>
          <button type="button" class="ec-chip" :class="{ on: showOptions }" @click="showOptions = !showOptions">
            <span class="ec-chip-k">Protection:</span>
            {{ protectionLabel }}
            <span class="ec-caret">▾</span>
          </button>
          <button type="button" class="ec-wrap mono" :class="{ on: wrap }" @click="wrap = !wrap" title="Toggle soft wrap">
            wrap {{ wrap ? 'on' : 'off' }}
          </button>
        </div>

        <!-- protection options -->
        <div v-if="showOptions" class="ec-options">
          <label class="control">
            <span class="control-label">View password <span class="optional">(optional)</span></span>
            <input
              v-model="passwordInput"
              type="password"
              class="lk-input control-input"
              maxlength="128"
              autocomplete="new-password"
              placeholder="Viewers must enter this"
            />
          </label>
          <label class="control burn-control">
            <input v-model="burnInput" type="checkbox" />
            <span>
              <span class="control-label">Burn after read</span>
              <span class="burn-hint">Self-destructs after it is viewed once. You keep access from your dashboard.</span>
            </span>
          </label>
        </div>

        <!-- editor -->
        <div class="ec-editor">
          <div v-if="!wrap" ref="gutter" class="ec-gutter mono" aria-hidden="true">
            <div v-for="n in Math.max(lineCount, 1)" :key="n">{{ n }}</div>
          </div>
          <textarea
            v-if="!previewOn"
            ref="ta"
            v-model="contentInput"
            class="ec-code mono"
            :class="{ over: overLimit }"
            :wrap="wrap ? 'soft' : 'off'"
            placeholder="Paste your text or code here…"
            spellcheck="false"
            @scroll="syncScroll"
          ></textarea>
          <!-- eslint-disable-next-line vue/no-v-html — hljs output is escaped -->
          <pre v-else class="ec-code ec-pre mono" @scroll="syncScroll"><code v-html="highlightedPreview"></code></pre>
        </div>

        <!-- footer -->
        <div class="ec-foot">
          <span class="ec-count mono">{{ lineCount }} {{ lineCount === 1 ? 'line' : 'lines' }} · {{ charCount }} chars<span v-if="overLimit" class="ec-over"> · over the 512 KB limit</span></span>
          <button type="button" class="lk-btn ec-preview-btn" :disabled="!contentInput" @click="previewOn = !previewOn">
            {{ previewOn ? 'Edit' : 'Preview' }}
          </button>
          <button class="lk-btn lk-btn-primary" :disabled="creating || !canSubmit" @click="createNewPaste">
            <span v-if="creating" class="btn-spinner"></span>
            <span>{{ creating ? 'Creating…' : 'Create paste →' }}</span>
          </button>
        </div>
      </div>

      <div v-if="error" class="paste-error">{{ error }}</div>
    </template>

    <!-- RESULT -->
    <div v-if="pasteResult" class="result-card">
      <div class="result-stripe"></div>
      <div class="result-body">
        <div class="result-main">
          <div class="result-ok"><span class="ok-badge">✓</span> Your paste is live</div>
          <div class="result-link">
            <span class="result-short mono">{{ shortDisplayVal }}</span>
            <CopyButton :text="shortUrlVal" small />
          </div>
          <div class="result-dest">
            <span class="faint">📋</span>
            <span class="ellipsis">{{ pasteResult.title || 'Untitled paste' }}</span>
            <span class="faint">· {{ languageLabelVal }} · {{ pasteResult.line_count }} lines</span>
          </div>
          <div v-if="!pasteResult.has_password && !pasteResult.burn_after_read" class="result-raw">
            <span class="faint">Raw:</span>
            <span class="mono ellipsis">{{ rawUrlVal }}</span>
            <CopyButton :text="rawUrlVal" small />
          </div>
          <div class="result-actions">
            <button v-if="authedNow" class="lk-btn" @click="viewPaste">Manage paste</button>
            <button class="lk-btn" @click="resetPaste">Create another</button>
          </div>
        </div>
        <div class="result-qr">
          <QrCode ref="qr" :value="shortUrlVal" :size="132" />
          <button class="lk-btn-ghost dl-qr" @click="downloadQr">↓ Download QR</button>
        </div>
      </div>
      <!-- Short link: an explicit opt-in, exactly like files. -->
      <div class="add-link-row">
        <template v-if="!linkResult">
          <span class="add-link-label">Add a short link to this paste</span>
          <div class="add-link-controls">
            <div class="slug-seg" :class="{ invalid: linkSlugInvalid }" title="Optional custom name — leave blank and we'll generate one">
              <span class="slug-host mono">{{ shortHost }}/</span>
              <input v-model="linkSlugInput" class="slug-input mono" placeholder="custom-name" maxlength="64" @keydown.enter="addShortLink" />
            </div>
            <button class="lk-btn lk-btn-primary" :disabled="linkCreating" @click="addShortLink">
              {{ linkCreating ? 'Creating…' : '+ Short link' }}
            </button>
          </div>
        </template>
        <template v-else>
          <span class="add-link-label">Short link</span>
          <div class="add-link-controls">
            <span class="mono add-link-result">{{ shortDisplay(linkResult.slug) }}</span>
            <CopyButton :text="shortUrl(linkResult.slug)" small />
          </div>
        </template>
      </div>
      <div v-if="linkError" class="paste-error">{{ linkError }}</div>
      <div v-if="pasteResult.claim_token" class="result-claim">
        <span class="claim-text">🔒 Sign in to keep this paste, edit its expiry &amp; track views.</span>
        <button class="lk-btn lk-btn-primary" @click="claim">Sign in &amp; claim</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import QrCode from '@/components/ui/QrCode.vue';
import CopyButton from '@/components/ui/CopyButton.vue';
import { shortUrl, shortDisplay, shortBase, isValidSlug, pasteUrl, pasteDisplay, pasteRawUrl } from '@/utils/links';
import { PASTE_LANGUAGES, MAX_PASTE_BYTES, byteLength, languageLabel } from '@/utils/pasteLanguages';
import { highlight } from '@/utils/hljs';

export default {
  name: 'PasteTool',
  components: { QrCode, CopyButton },
  data() {
    return {
      contentInput: '',
      languageInput: 'plain',
      titleInput: '',
      passwordInput: '',
      burnInput: false,
      expiryPreset: '30d',
      showOptions: false,
      wrap: false,
      previewOn: false,
      error: '',
      creating: false,
      pasteResult: null,
      // Opt-in short link (post-create, mirrors ShortenTool's file flow).
      linkSlugInput: '',
      linkCreating: false,
      linkResult: null,
      linkError: '',
      languages: PASTE_LANGUAGES,
      // Backend bounds are [1h, 30d]; 30 days is also the default when omitted.
      expiryPresets: [
        { key: '1h', label: '1 hour', hours: 1 },
        { key: '1d', label: '1 day', hours: 24 },
        { key: '7d', label: '7 days', hours: 24 * 7 },
        { key: '30d', label: '30 days (max)', hours: 24 * 30 },
      ],
    };
  },
  computed: {
    ...mapGetters('auth', { authedNow: 'isAuthenticated' }),
    contentBytes() { return byteLength(this.contentInput); },
    overLimit() { return this.contentBytes > MAX_PASTE_BYTES; },
    charCount() { return this.contentInput.length; },
    lineCount() {
      return this.contentInput ? this.contentInput.split('\n').length : 0;
    },
    canSubmit() { return !!this.contentInput.trim() && !this.overLimit; },
    protectionLabel() {
      if (this.passwordInput && this.burnInput) return 'Password + burn';
      if (this.passwordInput) return 'Password';
      if (this.burnInput) return 'Burn after read';
      return 'None';
    },
    linkSlugInvalid() {
      const slug = (this.linkSlugInput || '').trim();
      return !!slug && !isValidSlug(slug);
    },
    shortHost() { return shortBase().replace(/^https?:\/\//, ''); },
    highlightedPreview() {
      return highlight(this.contentInput, this.languageInput);
    },
    // The canonical paste page on this origin — the short link is opt-in.
    shortDisplayVal() { return this.pasteResult ? pasteDisplay(this.pasteResult.paste_id) : ''; },
    shortUrlVal() { return this.pasteResult ? pasteUrl(this.pasteResult.paste_id) : ''; },
    rawUrlVal() { return this.pasteResult ? pasteRawUrl(this.pasteResult.paste_id) : ''; },
    languageLabelVal() { return this.pasteResult ? languageLabel(this.pasteResult.language) : ''; },
  },
  methods: {
    ...mapActions('pastes', ['createPaste']),
    ...mapActions('shortLinks', ['createLink']),
    shortUrl,
    shortDisplay,
    // Keep the line-number gutter in step with the code area's scroll.
    syncScroll(e) {
      if (this.$refs.gutter) this.$refs.gutter.scrollTop = e.target.scrollTop;
    },
    expiresIso() {
      const preset = this.expiryPresets.find((p) => p.key === this.expiryPreset);
      const hours = preset ? preset.hours : 24 * 30;
      return new Date(Date.now() + hours * 3600 * 1000).toISOString();
    },
    async createNewPaste() {
      if (this.creating) return;
      this.error = '';

      if (!this.contentInput.trim()) { this.error = 'Paste something first'; return; }
      if (this.overLimit) { this.error = 'Paste is over the 512 KB limit'; return; }

      this.creating = true;
      try {
        this.pasteResult = await this.createPaste({
          content: this.contentInput,
          language: this.languageInput,
          title: this.titleInput.trim(),
          expires: this.expiresIso(),
          viewPassword: this.passwordInput || undefined,
          burnAfterRead: this.burnInput,
        });
      } catch (e) {
        // store surfaces a toast on failure
      } finally {
        this.creating = false;
      }
    },
    // Opt-in short link for the fresh paste — a separate Link row, exactly
    // like ShortenTool's post-upload file flow.
    async addShortLink() {
      if (this.linkCreating || !this.pasteResult) return;
      this.linkError = '';
      const slug = (this.linkSlugInput || '').trim();
      if (slug && !isValidSlug(slug)) {
        this.linkError = 'Custom name may only contain letters, numbers, - and _';
        return;
      }
      this.linkCreating = true;
      try {
        this.linkResult = await this.createLink({
          type: 'paste',
          pasteId: this.pasteResult.paste_id,
          slug,
        });
      } catch (e) {
        // store surfaces a toast on failure (e.g. slug already taken)
      } finally {
        this.linkCreating = false;
      }
    },
    resetPaste() {
      this.pasteResult = null;
      this.contentInput = '';
      this.titleInput = '';
      this.passwordInput = '';
      this.burnInput = false;
      this.expiryPreset = '30d';
      this.previewOn = false;
      this.error = '';
      this.linkSlugInput = '';
      this.linkResult = null;
      this.linkError = '';
    },
    viewPaste() {
      if (this.pasteResult) this.$router.push(`/dashboard/paste/${this.pasteResult.paste_id}`);
    },
    downloadQr() {
      if (this.$refs.qr) this.$refs.qr.download(`linker-${this.pasteResult.paste_id}-qr.png`);
    },
    claim() {
      // Sign in, land on this paste's detail page, and let it auto-claim with
      // the stashed claim token (?claim=1 is picked up there).
      this.$router.push({
        name: 'Login',
        query: { redirect: `/dashboard/paste/${this.pasteResult.paste_id}?claim=1` },
      });
    },
  },
};
</script>

<style scoped>
.paste-tool { width: 100%; }

/* ===== Editor card (mock F4) ===== */
.editor-card {
  background: var(--card-alt);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  animation: lk-pop .35s ease both;
  text-align: left;
}

/* Title row */
.ec-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid var(--border);
}
.ec-label {
  font-size: 11px;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: var(--faint);
  flex-shrink: 0;
}
.ec-title-input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  color: var(--text);
  font-size: 15px;
  font-weight: 600;
  padding: 5px 0;
}
.ec-title-input::placeholder { color: var(--faint); font-weight: 400; }
/* Controls row */
.ec-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border-bottom: 1px solid var(--border);
  background: var(--card);
  flex-wrap: wrap;
}
.ec-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text);
  background: var(--field);
  border: 1px solid var(--border-strong);
  border-radius: 5px;
  padding: 7px 12px;
  cursor: pointer;
  position: relative;
  font-family: inherit;
}
.ec-chip.on { border-color: var(--accent); }
.ec-chip-k { color: var(--faint); font-weight: 500; }
.ec-chip-select {
  appearance: none;
  -webkit-appearance: none;
  border: none;
  background: transparent;
  color: var(--text);
  font-size: 12.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  padding-right: 2px;
  max-width: 160px;
}
.ec-chip-select:focus { outline: none; }
.ec-caret { color: var(--faint); font-size: 10px; }
.ec-wrap {
  margin-left: auto;
  border: none;
  background: transparent;
  color: var(--faint);
  font-size: 11px;
  cursor: pointer;
  padding: 4px 2px;
}
.ec-wrap.on { color: var(--accent-h); font-weight: 600; }

/* Protection options */
.ec-options {
  display: flex;
  gap: 14px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--border);
  background: var(--card);
  flex-wrap: wrap;
  animation: lk-up .15s ease both;
}
.control { flex: 1; min-width: 200px; display: flex; flex-direction: column; gap: 5px; text-align: left; }
.control-label { font-size: 12px; font-weight: 600; color: var(--muted); }
.optional { font-weight: 400; color: var(--faint); }
.control-input { width: 100%; }
.burn-control { flex-direction: row; align-items: flex-start; gap: 9px; }
.burn-control input { margin-top: 3px; accent-color: var(--accent); }
.burn-hint { display: block; font-size: 11.5px; color: var(--faint); font-weight: 400; margin-top: 2px; }

/* Editor (mock F4): gutter + pre code area on the warm code surface */
.ec-editor {
  display: flex;
  align-items: stretch;
  background: var(--code-bg);
  min-height: 280px;
  max-height: 480px;
}
.ec-gutter {
  flex-shrink: 0;
  padding: 16px 0;
  text-align: right;
  color: var(--code-gutter);
  border-right: 1px solid var(--code-border);
  font-size: 12.5px;
  line-height: 1.75;
  user-select: none;
  overflow: hidden;
}
.ec-gutter div { padding: 0 14px; }
.ec-code {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  color: var(--text);
  font-size: 12.5px;
  line-height: 1.75;
  padding: 16px 18px;
  resize: none;
  outline: none;
  white-space: pre;
  overflow: auto;
  margin: 0;
}
.ec-code.over { color: var(--danger); }
.ec-code::placeholder { color: var(--faint); }
textarea.ec-code[wrap="soft"] { white-space: pre-wrap; }
.ec-pre code { font-family: inherit; white-space: pre; display: block; }

/* Constrained hljs palette (same recipe as PasteDetailView) */
.ec-pre :deep(.hljs-comment), .ec-pre :deep(.hljs-quote) { color: var(--faint); font-style: italic; }
.ec-pre :deep(.hljs-keyword), .ec-pre :deep(.hljs-selector-tag), .ec-pre :deep(.hljs-literal),
.ec-pre :deep(.hljs-doctag), .ec-pre :deep(.hljs-name), .ec-pre :deep(.hljs-built_in) { color: var(--accent); }
.ec-pre :deep(.hljs-string), .ec-pre :deep(.hljs-regexp), .ec-pre :deep(.hljs-addition) { color: var(--success); }
.ec-pre :deep(.hljs-number), .ec-pre :deep(.hljs-symbol), .ec-pre :deep(.hljs-meta), .ec-pre :deep(.hljs-link) { color: var(--warn); }
.ec-pre :deep(.hljs-title), .ec-pre :deep(.hljs-section), .ec-pre :deep(.hljs-attr) { color: var(--text); font-weight: 600; }

/* Footer */
.ec-foot {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 13px 20px;
  border-top: 1px solid var(--border);
  background: var(--card);
}
.ec-count { font-size: 11px; color: var(--faint); flex: 1; min-width: 0; }
.ec-over { color: var(--danger); font-weight: 700; }
.ec-preview-btn { padding: 9px 16px; }

.paste-error { margin-top: 10px; color: var(--danger); font-size: 12.5px; font-weight: 500; text-align: left; }

.btn-spinner {
  width: 15px; height: 15px;
  border: 2px solid rgba(255,255,255,.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: lk-spin .7s linear infinite;
}

/* ---- Result (mirrors ShortenTool) ---- */
.result-card {
  background: var(--card);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  animation: lk-pop .4s ease both;
}
.result-stripe { height: 5px; background: repeating-linear-gradient(90deg, var(--accent) 0 9px, transparent 9px 16px); }
.result-body { padding: 22px 24px; display: flex; gap: 22px; align-items: center; flex-wrap: wrap; }
.result-qr { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.dl-qr { border: none; background: transparent; color: var(--muted); font-size: 12px; font-weight: 600; cursor: pointer; }
.dl-qr:hover { color: var(--text); }
.result-main { flex: 1; min-width: 240px; }
.result-ok { display: flex; align-items: center; gap: 8px; color: var(--success); font-size: 12.5px; font-weight: 600; margin-bottom: 12px; }
.ok-badge { width: 18px; height: 18px; border-radius: 50%; background: var(--success); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 11px; }
.result-link {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 12px 14px;
  margin-bottom: 12px;
}
.result-short { flex: 1; font-size: 16px; font-weight: 600; color: var(--accent); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.result-dest { font-size: 12.5px; color: var(--muted); display: flex; align-items: center; gap: 6px; margin-bottom: 8px; overflow: hidden; }
.result-raw { font-size: 12px; color: var(--muted); display: flex; align-items: center; gap: 6px; margin-bottom: 14px; overflow: hidden; }
.result-actions { display: flex; gap: 9px; flex-wrap: wrap; }

/* Opt-in short link row (mirrors ShortenTool's add-link-row) */
.add-link-row {
  border-top: 1px solid var(--border);
  padding: 14px 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.add-link-label { font-size: 13px; font-weight: 600; color: var(--muted); flex: 1; min-width: 180px; }
.add-link-controls { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.add-link-result { font-size: 14px; font-weight: 600; color: var(--accent); }
.slug-seg {
  display: flex;
  align-items: center;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0 10px;
  transition: border-color .15s, box-shadow .15s;
}
.slug-seg:focus-within { border-color: var(--accent); box-shadow: 0 0 0 3px var(--ring); }
.slug-seg.invalid { border-color: var(--danger); }
.slug-host { color: var(--faint); font-size: 12.5px; white-space: nowrap; }
.slug-input {
  width: 11.5ch;
  border: none;
  background: transparent;
  color: var(--text);
  font-size: 13px;
  padding: 9px 2px;
}

.result-claim {
  border-top: 1px solid var(--border);
  background: var(--accent-soft);
  padding: 14px 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.claim-text { font-size: 13px; color: var(--text); font-weight: 500; flex: 1; min-width: 200px; }

.faint { color: var(--faint); }
.ellipsis { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mono { font-family: var(--font-mono); }

/* ---- Mobile (mock F4m): chips wrap, smaller type ---- */
@media (max-width: 560px) {
  .ec-title-row { flex-wrap: wrap; }
  .ec-editor { min-height: 200px; }
  .ec-code, .ec-gutter { font-size: 11px; }
  .ec-foot { flex-wrap: wrap; }
  .ec-count { flex-basis: 100%; }
}
</style>
